﻿//this store module handles user graph repo
import _ from "lodash-es";
import router, {ROUTE_NAMES} from "@/src/router";
import * as graphsApi from "@/src/api/graphs";
import {OP, trackPending} from "@/src/queries/pending";
import * as workspacesApi from "@/src/api/workspaces";
import * as celonisApi from "@/src/api/celonis";
import {
    fetchActivities,
    fetchActivityTableOptions,
    fetchCaseTables,
    fetchDatamodels,
    fetchDatapools,
    fetchTableColumns,
    fetchTables,
    fetchTemplates,
} from "@/src/queries/celonis";
import {GRAPH_PERMISSION, WORKSPACE_PERMISSION} from "@/src/model/Permission";
import WorkspaceModel from "@/src/model/WorkspaceModel";
import {activityEntries, graphActivityRequest} from "@/src/utils/connectorSchema";

const LATEST_VIEWED_GRAPHS_KEY = 'latestViewedGraphs'

const updateLatestViewedGraphs = (graphId) => {
    if (!graphId) {
        return
    }
    const graphIdValue = String(graphId)
    const latestViewedGraphs = (JSON.parse(localStorage.getItem(LATEST_VIEWED_GRAPHS_KEY)) || []).map(id => String(id));
    if (!latestViewedGraphs.includes(graphIdValue)) {
        if (latestViewedGraphs.length > 3) {
            latestViewedGraphs.pop();
        }
    } else {
        latestViewedGraphs.splice(latestViewedGraphs.indexOf(graphIdValue), 1);
    }
    latestViewedGraphs.unshift(graphIdValue);
    localStorage.setItem(LATEST_VIEWED_GRAPHS_KEY, JSON.stringify(latestViewedGraphs));
}

const removeLatestViewedGraphs = (graphIds) => {
    if (!graphIds || graphIds.length === 0) {
        return
    }
    const graphIdsToRemove = graphIds.map(id => String(id))
    const latestViewedGraphs = (JSON.parse(localStorage.getItem(LATEST_VIEWED_GRAPHS_KEY)) || []).map(id => String(id));
    const filtered = latestViewedGraphs.filter(id => !graphIdsToRemove.includes(String(id)));
    localStorage.setItem(LATEST_VIEWED_GRAPHS_KEY, JSON.stringify(filtered));
}
export default {
    namespaced: true,
    state: {
        graphTree: [],
        selectedGraphId: null,
        unsavedGraph: false,
        activities: [],
        tables: [],
        versions: [],
        selectedVersion: 'draft',
        caseTables: [],
        datapools: [],
        workspaceUserPermissions: [],
        graphUserPermissions: [],
        graphResults: new Map(),
        graphTableOptions: [],
        templates: [],
        templateServiceError: null,
        currentGraph: null, // used to store the current graph data
        graphMap: new Map(),
        selectedMenu: null,
        exerciseGraphCache: new Map(), // cache to store exercise graphs by graphId
        missingTrainingProcesses: [],
        graphOrder: {},
        tableContext: {
            datapoolId: null,
            datamodelId: null
        }
    },
    mutations: {
        setUnsavedGraph: (state, value) => {
            state.unsavedGraph = value
        },
        setSelectedVersion: (state, value) => {
            state.selectedVersion = value
        },

        setGraphId: (state, value) => {
            state.selectedGraphId = value
        },
        setGraphTree: (state, value) => {
            state.graphTree = value.map(w => {
                return new WorkspaceModel(null, w, state.graphMap)
            })
        },
        setGraphResults: (state, {spaceId, value}) => {
            state.graphResults.set(spaceId, value)
            // update results in graphMap for easy access
            value.forEach(({graph_id, result}) => {
                //update result propety in the graph object
                const graph = state.graphMap.get(graph_id)
                if (graph) {
                    graph.result = result
                } 
                
            })
        },
        setTemplates: (state, value) => {
            if (value.error) {
                console.error('Error loading templates:', value.error)
                state.templateServiceError = value.error
                return
            }
            state.templates = value.map(ws => {
                return Object.assign({}, ws, {
                    key: ws.workspace_id,
                    label: ws.workspace_name,
                    leaf: false,
                    selectable: false,
                    children: ws.graphs.map(g => {
                        return Object.assign({}, g, {
                            key: ws.workspace_id + '_' + g.id,
                            label: g.name,
                            leaf: true,
                            selectable: true
                        })
                    })
                })
            })
        },
        setSelectedMenu: (state, value) => {
            state.selectedMenu = value
        },
        setMissingTrainingProcesses: (state, value) => {
            state.missingTrainingProcesses = value
        },
        setGraphOrder: (state, {workspaceId, order}) => {
            state.graphOrder = {
                ...state.graphOrder,
                [workspaceId]: order,
            }
        }
    },
    actions: {
        loadWorkspaces: async ({dispatch, commit, rootGetters}) => {
            dispatch('loadTemplates')
            commit('setGraphTree', await trackPending(OP.GET_WORKSPACES, () => workspacesApi.listWorkspaces()))
            if (rootGetters['userStore/isTrainee']) {
                dispatch('loadMissingTrainingProcesses')
            } else {
                commit('setMissingTrainingProcesses', [])
            }
        },
        loadGraphResults: async ({state, commit}, spaceId) => {
            if (state.graphResults.has(spaceId)) {
                return
            }
            const result = await graphsApi.getGraphResults(spaceId);
            commit('setGraphResults', {spaceId, value: result})
        },
        addSpace: async ({dispatch}, data) => {
            await trackPending(OP.ADD_WORKSPACE, () => workspacesApi.createWorkspace(data))
            dispatch('loadWorkspaces')
        },
        addGraph: async ({state, dispatch}, payload) => {
            const data = Object.assign({
                pac_project_key: '',
                definition: {}
            }, payload)
            const graphResult = await graphsApi.createGraph(data.workspace_id, data)
            await dispatch('loadWorkspaces')
            const graph = state.graphMap.get(graphResult.id)
            await dispatch('setSelectedGraph', graph)
            dispatch('graphStore/toggleEditMode', true, {root: true})
        },
        updateWorkspace: async ({dispatch}, {workspaceId, data}) => {
            await workspacesApi.updateWorkspace(workspaceId, data)
            dispatch('loadWorkspaces')
        },
        cleanUpResults: async ({state, getters}, data) => {
            let graph = getters.getGraph;
            if (!graph) {
                return
            }
            await graphsApi.cleanUpResults(state.selectedGraphId, data)
            Object.assign(graph, data)
        },
        setSelectedSpace: async ({state, dispatch, commit, rootGetters}, spaceId) => {
            if (state.unsavedGraph) {
                return
            }
            state.workspaceUserPermissions = []
            const isEditMode = rootGetters['graphStore/getEditMode']
            if (isEditMode) {
                //toggle edit mode off to unlock the graph
                await dispatch('graphStore/toggleEditMode', {}, {root: true})
            }
            if (!spaceId) {
                router.push({name: ROUTE_NAMES.REPO})
                return
            }
            router.push({name: ROUTE_NAMES.REPO, params: {spaceId: spaceId}})
            await dispatch('loadUserWorkspacePermissions', spaceId)
            //do not await for it
            dispatch('loadGraphResults', spaceId)
        },
        getGraph: async ({state, getters}, {graphId}) => {
            try {
                return await trackPending(OP.GET_GRAPH, () => graphsApi.getGraph(graphId))
            } catch (error) {
                if (error?.response?.status === 404) {
                    router.replace({ name: ROUTE_NAMES.GRAPH_NOT_FOUND })
                    return null
                }
                throw error
            }
        },
        // refactor this mess
        setSelectedGraph: async ({state, dispatch, commit, rootGetters}, graph) => {
            state.graphUserPermissions = []
            if (state.unsavedGraph) {
                return
            }

            const isEditMode = rootGetters['graphStore/getEditMode']
            if (isEditMode) {
                //toggle edit mode off to unlock the graph
                await dispatch('graphStore/toggleEditMode', {}, {root: true})
            }

            if (!graph) {
                commit('setGraphId', null)
                return
            }
            commit('setGraphId', graph.id)
            updateLatestViewedGraphs(graph.id)
            // make sure graph is GraphModel instance
            await dispatch('updateStoredGraph', graph)
            await dispatch('loadUserGraphPermissions')
            const graphData = await dispatch('getGraph', {
                graphId: graph.id
            })
            if (!graphData) {
                return
            }
            //NOTE: don't remember why we were picking only some fields, monitor for error. In any case those fields are not enough
            // await dispatch('updateStoredGraph', _.pick(graphData, ['definition', 'pac_project_key', 'locked_by', 'locked_date', 'datapool_id', 'datamodel_id', 'notes']))
            await dispatch('updateStoredGraph', graphData)
            router.push({
                name: ROUTE_NAMES.GRAPH, params: {
                    graphId: graph.id
                }
            })
            await dispatch('loadVersions')

            commit('setSelectedVersion', 'draft')
        },
        updateStoredGraph: async ({state, getters}, data) => {
            const graphModel = state.graphMap.get(parseInt(data.id))
            state.currentGraph = Object.assign({}, state.currentGraph, data, {parent: graphModel.parent})
        },
        deleteWorkspace: async ({state, dispatch, commit}, ws) => {
            await trackPending(OP.DELETE_WORKSPACE, () => workspacesApi.deleteWorkspace(ws.id))
            dispatch('loadWorkspaces')
        },
        deleteGraph: async ({state, dispatch, commit}, graphId) => {
            await trackPending(OP.DELETE_GRAPH, () => graphsApi.deleteGraph(graphId))
            if (state.selectedGraphId === graphId) {
                commit('setGraphId', null)
            }
            removeLatestViewedGraphs([graphId])
            dispatch('loadWorkspaces')
        },
        deleteGraphs: async ({state, dispatch}, graphIds) => {
            await graphsApi.deleteGraphs(graphIds)
            if (state.selectedGraphId && graphIds.includes(state.selectedGraphId)) {
                dispatch('setSelectedGraph', null)
            }
            removeLatestViewedGraphs(graphIds)
            dispatch('loadWorkspaces')
        },
        publishGraph: async ({state, dispatch}, description = '') => {
            await trackPending(OP.PUBLISH_GRAPH, () => graphsApi.publishGraph(state.selectedGraphId, description))
            dispatch('loadVersions')
        },
        cancelJob: async ({state}) => {
            return await trackPending(OP.CANCEL_JOB, () => graphsApi.cancelJob(state.selectedGraphId))
        },
        runGraphs: async ({dispatch}, graphIds) => {
            const response = await trackPending(OP.RUN_JOBS, () => graphsApi.runJobs(graphIds))
            await dispatch('loadWorkspaces')
            return response
        },

        getDatapools: async ({state}, force = false) => {
            if (state.datapools.length == 0 || force) {
                state.datapools = await fetchDatapools()
            }
            return state.datapools
        },
        getDatamodels: async ({state}, datapoolId) => {
            const datapool = _.find(state.datapools, d => d.id == datapoolId)
            if (datapool && datapool.hasOwnProperty('datamodels')) {
                return datapool.datamodels
            }
            const datamodels = await fetchDatamodels(datapoolId)
            datapool.datamodels = datamodels
            return datamodels
        },
        loadActivities: async ({state, getters, dispatch, rootGetters}) => {
            const graph = getters.getGraph
            state.activities = []
            state.tables = []
            state.tableContext = {
                datapoolId: null,
                datamodelId: null
            }
            // Workspaces reading from another connector: activity names from the data connection
            const workspaceDatasource = graph?.parent?.datasource
            if (workspaceDatasource?.input?.connection_id) {
                await dispatch('connectionStore/loadAll', null, {root: true})
                const connector = rootGetters['connectionStore/connectorOfConnection'](workspaceDatasource.input.connection_id)
                const request = graphActivityRequest(connector, workspaceDatasource, graph.datasource)
                if (request) {
                    try {
                        const options = await dispatch('connectionStore/loadOptions', request, {root: true})
                        state.activities = activityEntries(options, request.params)
                    } catch (error) {
                        console.warn('Activities could not be loaded from the data connection', error)
                    }
                    return
                }
            }
            if (graph && graph.datapool_id && graph.datamodel_id) {
                state.activities = await fetchActivities(graph.datapool_id, graph.datamodel_id)

                state.tables = await fetchTables(graph.datapool_id, graph.datamodel_id)
                state.tableContext = {
                    datapoolId: graph.datapool_id,
                    datamodelId: graph.datamodel_id
                }
            }

        },
        loadDatamodelContext: async ({state, dispatch}, {datapoolId, datamodelId}) => {
            state.activities = []
            state.tables = []
            state.tableContext = {
                datapoolId: null,
                datamodelId: null
            }
            if (!datapoolId || !datamodelId) {
                return
            }

            state.activities = await fetchActivities(datapoolId, datamodelId)
            state.tables = await fetchTables(datapoolId, datamodelId)
            state.tableContext = {
                datapoolId,
                datamodelId
            }
        },
        loadVersions: async ({state}) => {
            state.versions = []
            state.versions = await graphsApi.listGraphVersions(state.selectedGraphId)
        },
        restoreVersion: async ({state, commit, dispatch}) => {
            const res = await graphsApi.restoreGraphVersion(state.selectedGraphId, state.selectedVersion)
            await dispatch('updateStoredGraph', {definition: res.definition})
            commit('setSelectedVersion', 'draft')
        },
        //used in CaseTableSelector as 'load' + prop
        loadCaseTables: async ({state}, {datapoolId, datamodelId}) => {
            state.caseTables = []
            state.caseTables = await fetchCaseTables(datapoolId, datamodelId)
        },
        loadUserWorkspacePermissions: async ({state}, workspaceId) => {
            state.workspaceUserPermissions = (await workspacesApi.getWorkspaceUserPermissions(workspaceId)).permissions
        },
        loadUserGraphPermissions: async ({state}) => {
            const res = (await graphsApi.getGraphUserPermissions(state.selectedGraphId))
            state.graphUserPermissions = res.permissions
        },
        getTableColumns: async ({state, getters}, tableInput) => {
            const isPayloadObject = tableInput && typeof tableInput === 'object'
            const tableName = isPayloadObject ? tableInput.tableName : tableInput
            const tableObject = _.find(state.tables, table => table.name === tableName)
            if (!tableObject) {
                return []
            }

            if (tableObject && tableObject.hasOwnProperty('columns')) {
                return tableObject.columns
            }
            const graph = getters.getGraph
            const datapoolId = isPayloadObject ? tableInput.datapoolId : null
            const datamodelId = isPayloadObject ? tableInput.datamodelId : null
            const effectiveDatapoolId = datapoolId || state.tableContext?.datapoolId || graph?.datapool_id
            const effectiveDatamodelId = datamodelId || state.tableContext?.datamodelId || graph?.datamodel_id

            if (!effectiveDatapoolId || !effectiveDatamodelId) {
                return []
            }
            const columns = await fetchTableColumns(effectiveDatapoolId, effectiveDatamodelId, tableObject.id)
            tableObject.columns = columns
            return columns.sort()
        },
        validatePql: async ({getters, state}, payload) => {
            const pql = typeof payload === 'string' ? payload : payload?.pql
            const graph = getters.getGraph
            const datapoolId = payload?.datapoolId || state.tableContext?.datapoolId || graph?.datapool_id
            const datamodelId = payload?.datamodelId || state.tableContext?.datamodelId || graph?.datamodel_id

            if (graph && graph.datapool_id && graph.datamodel_id || (datapoolId && datamodelId)) {
                return await trackPending(OP.VALIDATE_PQL, () => celonisApi.validatePql(datapoolId, datamodelId, pql))
            } else {
                return {
                    state: 'ERROR',
                    errors: ['Datamodel not set']
                }
            }
        },
        //used in CaseTableSelector as 'load' + prop
        loadGraphTableOptions: async ({state}, {datapoolId, datamodelId}) => {
            state.graphTableOptions = [{name: null}]
            const options = await fetchActivityTableOptions(datapoolId, datamodelId)
            state.graphTableOptions = options
        },
        loadTemplates: async ({commit}) => {
            const templates = await fetchTemplates()
            commit('setTemplates', templates)
        },
        moveGraphs: async ({state, dispatch}, data) => {
            await graphsApi.moveGraphs(data)
            await dispatch('loadWorkspaces')
        },
        createUsersTrainingFolder: async ({state, dispatch}, { workspaceId, emailList }) => {
            await trackPending(OP.CREATE_USERS_TRAINING_FOLDER, () => workspacesApi.createUsersTrainingFolder(workspaceId, emailList))
            await dispatch('loadWorkspaces')
        },
        getExerciseGraph: async ({state, dispatch}) => {
            const graphId = state.selectedGraphId
            if (state.exerciseGraphCache.has(graphId)) {
                return state.exerciseGraphCache.get(graphId)
            }
            const graphData = await trackPending(OP.GET_EXERCISE_GRAPH, () => graphsApi.getExerciseGraph(graphId))
            state.exerciseGraphCache.set(graphId, graphData)
            return graphData
        },
        startTrainingFolder: async ({dispatch}, {workspaceId}) => {
            const createdWorkspace = await workspacesApi.startTrainingFolder(workspaceId)
            await dispatch('loadWorkspaces')
            return createdWorkspace?.workspace_id
        },
        loadMissingTrainingProcesses: async ({commit}) => {
            const processes = await workspacesApi.listMissingTrainingProcesses()
            commit('setMissingTrainingProcesses', processes)
        },
        loadGraphOrder: async ({state, commit}, workspaceId) => {
            if (state.graphOrder[workspaceId]) {
                return state.graphOrder[workspaceId]
            }

            const order = await graphsApi.getGraphOrder(workspaceId)
            commit('setGraphOrder', {workspaceId, order})
            return order
        },
        updateGraphOrder: async ({commit}, {workspaceId, order}) => {
            commit('setGraphOrder', {workspaceId, order})
            await graphsApi.updateGraphOrder(workspaceId, order)
        },
    },
    getters: {
        graphTree: state => state.graphTree || [],
        getGraph: (state) => {
            return state.currentGraph
        },
        getGraphDefinition: (state, getters) => {
            const graph = getters.getGraph;
            if (_.isEmpty(graph)) {
                return null
            }
            if (state.selectedVersion === 'draft') {
                return graph.definition
            }
            const foundVersion = _.find(state.versions, v => v.id == state.selectedVersion)
            if (!foundVersion) {
                return graph.definition
            }
            return foundVersion.definition
        },
        getVersions: (state) => {
            return state.versions
        },
        getSelectedVersion: (state) => {
            return state.selectedVersion
        },
        latestVersion: (state) => {
            return state.versions.length > 0 ? state.versions[0] : null
        },
        getActivityTables: state => {
            return state.activities
        },
        getActivities: (state) => {
            return state.activities.map(item => item.activity)
        },
        getTables: (state) => {
            return state.tables
        },
        getActivityTable: (state) => (name) => {
            const activity = _.find(state.activities, item => item.activity === name)
            return activity ? activity.table : ''
        },
        hasDatamodel: (state) => {
            return state.currentGraph && state.currentGraph.datamodel_id
        },
        //used in CaseTableSelector as 'get' + prop
        getCaseTables: state => state.caseTables,
        findDatapool: state => datamodelId => {
            for (let pool of state.datapools) {
                if (!pool.datamodels) {
                    continue
                }
                const found = _.find(pool.datamodels, model => model.id === datamodelId)
                if (found) {
                    return pool.id
                }
            }
            return null
        },
        getDatamodelLabel: state => datamodelId => {
            for (let pool of state.datapools) {
                if (!pool.datamodels) {
                    continue
                }
                const found = _.find(pool.datamodels, model => model.id === datamodelId)
                if (found) {
                    return found.name
                }
            }
            return null
        },
        getGraphUserPermissions: (state) => state.graphUserPermissions,
        getWorkspaceUserPermissions: (state) => state.workspaceUserPermissions,
        canEditGraph: (state, getters, rootState, rootGetters) => {
            const graph = getters.getGraph
            if (!graph) {
                return false
            }
            const workspace = graph.parent
            return rootGetters['userStore/userCan'](GRAPH_PERMISSION.EDIT_GRAPH, graph) || rootGetters['userStore/userCan'](WORKSPACE_PERMISSION.EDIT_ALL_GRAPHS, workspace)
        },
        getGraphResults: (state) => (spaceId) => {
            return state.graphResults.get(spaceId) || []
        },
        //used in CaseTableSelector as 'get' + prop
        getGraphTableOptions: state => leadingObjectTable => state.graphTableOptions[leadingObjectTable],
        selectedMenu: state => state.selectedMenu,
        missingTrainingProcesses: state => state.missingTrainingProcesses,
        graphOrderByWorkspace: state => workspaceId => state.graphOrder[workspaceId] || [],
        workspaceTree: (state, getters, rootState, rootGetters) => {
            //recursively filter the graphTree, keep only workspaces, remove grap leaves
            const userCan = rootGetters['userStore/userCan']
            const filterWorkspaces = (tree) => {
                return tree.filter(item => item instanceof WorkspaceModel).map(item => {
                    if (item.children && item.children.length > 0) {
                         let children = filterWorkspaces(item.children)
                         return Object.assign({}, item, {
                             children,
                             leaf: children.length === 0,
                             selectable: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item)
                        })
                    }
                    return Object.assign({}, item, {
                        children: [],
                        leaf: true,
                        styleClass: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item) ? '' : 'bg-gray-100 cursor-not-allowed',
                        disabled: !userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item),
                        selectable: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item)
                    })
                })
            }
            return filterWorkspaces(_.cloneDeep(state.graphTree))
        }
    }
}
